import { ExamCenterProctorType } from '@exam-center/graphql';
import { PHONE_NUMBER_MASK } from '@exam-center/utils';
import {
  englishTitleOptionSchema,
  thaiTitleOptionSchema,
  TITLE_REQUIRED_ERROR_MESSAGE,
} from '@shared/utils';
import dayjs from 'dayjs';
import IMask from 'imask';
import { z } from 'zod';

const NAME_TH_PATTERN = /^[\u0E00-\u0E7F\s]+$/;
const NAME_EN_PATTERN = /^[A-Za-z\s]+$/;
const MIN_REQUIRED_FIELD_LENGTH = 1;
const MAX_EMAIL_LENGTH = 256;
const MAX_NAME_LENGTH = 100;
const MAX_ADDRESS_LENGTH = 1024;
export const AdminProctorDetailsFormSchema = z
  .object({
    proctorType: z.nativeEnum(ExamCenterProctorType, {}),
    titleTh: thaiTitleOptionSchema.nullish(),
    firstNameTh: z.string().trim().nullish(),
    lastNameTh: z.string().trim().nullish(),
    email: z.string().trim().optional(),
    titleEn: englishTitleOptionSchema.nullish(),
    firstNameEn: z.string().trim().nullish(),
    lastNameEn: z.string().trim().nullish(),
    phoneNumber: z
      .string()
      .nullish()
      .refine(
        (val) => {
          if (!val) return true;
          const mask = IMask.createMask({ mask: PHONE_NUMBER_MASK });
          mask.resolve(val);
          return mask.isComplete;
        },
        { message: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง' },
      ),
    address: z
      .string()
      .trim()
      .max(MAX_ADDRESS_LENGTH, `ความยาวต้องไม่เกิน ${MAX_ADDRESS_LENGTH} ตัวอักษร`),
    employeeInfo: z.object({
      code: z.string().trim().nullish(),
      position: z.string().trim().nullish(),
      department: z.string().trim().nullish(),
      termination: z.object({
        isAssigned: z.enum(['true', 'false']),
        endedAt: z.date().nullish(),
      }),
      startedAt: z.date().nullish(),
    }),
  })
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) return true;
      return Boolean(data.titleEn?.value && data.titleEn?.label);
    },
    { message: TITLE_REQUIRED_ERROR_MESSAGE, path: ['titleEn'] },
  )
  .refine(
    (data) => {
      return Boolean(data.titleTh?.value && data.titleTh?.label);
    },
    { message: TITLE_REQUIRED_ERROR_MESSAGE, path: ['titleTh'] },
  )
  .refine(
    (data) => {
      const val = data.firstNameTh;

      if (!val) return false;
      if (val.trim().length < MIN_REQUIRED_FIELD_LENGTH) return false;
      return true;
    },
    { message: 'กรุณากรอกชื่อภาษาไทย', path: ['firstNameTh'] },
  )
  .refine(
    (data) => {
      const val = data.lastNameTh;

      if (!val) return false;
      if (val.trim().length < MIN_REQUIRED_FIELD_LENGTH) return false;
      return true;
    },
    { message: 'กรุณากรอกนามสกุลภาษาไทย', path: ['lastNameTh'] },
  )
  .refine(
    (data) => {
      const val = data.firstNameTh;
      if (val) {
        return val.trim().length <= MAX_NAME_LENGTH;
      }
      return true;
    },
    { message: `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`, path: ['firstNameTh'] },
  )
  .refine(
    (data) => {
      const val = data.lastNameTh;
      if (val) {
        return val.trim().length <= MAX_NAME_LENGTH;
      }
      return true;
    },
    { message: `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`, path: ['lastNameTh'] },
  )
  .refine(
    (data) => {
      const val = data.firstNameTh;
      if (val) {
        return NAME_TH_PATTERN.test(val);
      }
      return true;
    },
    { message: 'กรุณากรอกชื่อเป็นภาษาไทย', path: ['firstNameTh'] },
  )
  .refine(
    (data) => {
      const val = data.lastNameTh;
      if (val) {
        return NAME_TH_PATTERN.test(val);
      }
      return true;
    },
    { message: 'กรุณากรอกนามสกุลเป็นภาษาไทย', path: ['lastNameTh'] },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) return true;
      const val = data.firstNameEn;

      if (!val) return false;
      if (val.trim().length < MIN_REQUIRED_FIELD_LENGTH) return false;
      return true;
    },
    { message: 'กรุณากรอกชื่อภาษาอังกฤษ', path: ['firstNameEn'] },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) return true;
      const val = data.lastNameEn;

      if (!val) return false;
      if (val.trim().length < MIN_REQUIRED_FIELD_LENGTH) return false;
      return true;
    },
    { message: 'กรุณากรอกนามสกุลภาษาอังกฤษ', path: ['lastNameEn'] },
  )
  .refine(
    (data) => {
      const val = data.firstNameEn;
      if (val) {
        return val.trim().length <= MAX_NAME_LENGTH;
      }
      return true;
    },
    { message: `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`, path: ['firstNameEn'] },
  )
  .refine(
    (data) => {
      const val = data.lastNameEn;
      if (val) {
        return val.trim().length <= MAX_NAME_LENGTH;
      }
      return true;
    },
    { message: `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`, path: ['lastNameEn'] },
  )
  .refine(
    (data) => {
      const val = data.firstNameEn;
      if (val) {
        return NAME_EN_PATTERN.test(val);
      }
      return true;
    },
    { message: 'กรุณากรอกชื่อเป็นภาษาอังกฤษ', path: ['firstNameEn'] },
  )
  .refine(
    (data) => {
      const val = data.lastNameEn;
      if (val) {
        return NAME_EN_PATTERN.test(val);
      }
      return true;
    },
    { message: 'กรุณากรอกนามสกุลเป็นภาษาอังกฤษ', path: ['lastNameEn'] },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) {
        return Boolean(data.email && data.email.trim().length > 0);
      }
      return true;
    },
    { message: 'กรุณากรอกอีเมลผู้ใช้', path: ['email'] },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) return true;
      const { termination } = data.employeeInfo;
      if (!termination) return false;
      if (termination.isAssigned === 'true') {
        return Boolean(termination.endedAt);
      }
      return true;
    },
    {
      message: 'ระบุวันที่สิ้นสุดปฏิบัติหน้าที่',
      path: ['employeeInfo', 'termination', 'endedAt'],
    },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) {
        if (!data.email || data.email.trim().length === 0) return true;
        return data.email.trim().length <= MAX_EMAIL_LENGTH;
      }
      return true;
    },
    { message: `ความยาวต้องไม่เกิน ${MAX_EMAIL_LENGTH} ตัวอักษร`, path: ['email'] },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) {
        if (!data.email || data.email.trim().length === 0) return true;
        return z.string().email().safeParse(data.email).success;
      }
      return true;
    },
    { message: 'รูปแบบอีเมลไม่ถูกต้อง', path: ['email'] },
  )
  .refine(
    (data) => {
      if (data.proctorType === ExamCenterProctorType.Proctor_3) return true;
      const { termination, startedAt } = data.employeeInfo;
      if (termination.isAssigned === 'true' && termination.endedAt) {
        return (
          dayjs(termination.endedAt).isAfter(dayjs(startedAt), 'date') ||
          dayjs(termination.endedAt).isSame(dayjs(startedAt), 'date')
        );
      }
      return true;
    },
    {
      message: 'วันที่สิ้นสุดปฏิบัติหน้าที่ต้องมากกว่าหรือเท่ากับวันที่เริ่มปฏิบัติหน้าที่',
      path: ['employeeInfo', 'termination', 'endedAt'],
    },
  );

export type AdminProctorDetailsFormInput = z.infer<typeof AdminProctorDetailsFormSchema>;
